import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";

import { useApp } from "../context/AppContext";
import { SERVICE_LOCATION, SAVE_CLIENT_LOGIN } from "../constants/endpoints";

const Profile = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { setLoading, showToast } = useApp();

    const { data } = location.state || {};
    const details = data?.data?.clientDetails || {};
    const isAdmin = details?.isAdmin === true;

    // ── Form state ─────────────────────────────────────────────
    const [id, setId] = useState(0);
    const [clientAccount, setClientAccount] = useState("");
    const [userDisplayName, setUserDisplayName] = useState("");
    const [userPhone, setUserPhone] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [userName, setUserName] = useState("");
    const [passwordHash, setPasswordHash] = useState("");
    const [isPasswordChange, setIsPasswordChange] = useState(false);
    const [password, setPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    // ── Load on mount ──────────────────────────────────────────
    const onLoad = useCallback(() => {
        setId(details.id);
        setClientAccount(details.clientAccount);
        setUserDisplayName(details.userDisplayName || "");
        setUserPhone(details.userPhone || "");
        setUserEmail(details.userEmail || "");
        setUserName(details.userName || "");
        setPasswordHash(details.passwordHash || "");
    }, [details]);

    useEffect(() => { onLoad(); }, [onLoad]);

    // ── Save ───────────────────────────────────────────────────
    const handleSave = async () => {
        setLoading(true);
        try {
            const res = await axios.post(
                SERVICE_LOCATION + SAVE_CLIENT_LOGIN,
                {
                    id, clientAccount, userName, passwordHash,
                    userDisplayName, userPhone, userEmail,
                    existingPassword: password,
                    newPassword, confirmPassword, isPasswordChange,
                },
                { headers: { Authorization: `Bearer ${data.data.token}` } }
            );
            setLoading(false);
            if (res.data.isSuccess) {
                const updatedData = {
                    ...data,
                    data: {
                        ...data.data,
                        clientDetails: {
                            ...data.data.clientDetails,
                            passwordHash: res.data.data.passwordHash,
                            userDisplayName, userPhone, userEmail,
                        },
                    },
                };
                showToast("success", "Profile updated successfully.");
                const returnRoute = isAdmin ? "/home/admin" : "/home/dashboard";
                navigate(returnRoute, { state: { data: updatedData } });
            } else {
                showToast("error", res.data.errorMessage || "Save failed.");
            }
        } catch {
            setLoading(false);
            showToast("error", "Network error. Please try again.");
        }
    };

    const handleCancel = () => {
        const returnRoute = isAdmin ? "/home/admin" : "/home/dashboard";
        navigate(returnRoute, { state: { data } });
    };

    const handleCreateLogin = () => {
        navigate("/home/createlogin", { state: { data } });
    };

    return (
        <div className="page-enter" style={{ maxWidth: 680 }}>
            <h1 style={styles.heading}>User Profile</h1>
            <p style={styles.sub}>Update your contact details or change your password.</p>

            {/* Create Login option for Admin */}
            {isAdmin && (
                <div style={styles.adminActions}>
                    <span style={styles.adminActionsLabel}>
                        <i className="pi pi-shield" style={{ fontSize: 12 }} /> Admin Actions
                    </span>
                    <Button
                        label="Create Client Login"
                        icon="pi pi-user-plus"
                        outlined
                        onClick={handleCreateLogin}
                        style={styles.adminBtn}
                    />
                </div>
            )}

            <div style={styles.card}>
                {/* ── Section label: Contact Details ── */}
                <p style={styles.sectionTitle}>Contact Details</p>

                {/* Display Name */}
                <div style={styles.fieldRow}>
                    <label style={styles.fieldLabel}>Display Name</label>
                    <div style={styles.fieldInput}>
                        <InputText
                            value={userDisplayName}
                            onChange={e => setUserDisplayName(e.target.value)}
                            placeholder="Your display name"
                            style={styles.input}
                        />
                    </div>
                </div>

                {/* Phone */}
                <div style={styles.fieldRow}>
                    <label style={styles.fieldLabel}>Phone</label>
                    <div style={styles.fieldInput}>
                        <InputText
                            value={userPhone}
                            onChange={e => setUserPhone(e.target.value)}
                            placeholder="07..."
                            style={styles.input}
                        />
                    </div>
                </div>

                {/* Email */}
                <div style={styles.fieldRow}>
                    <label style={styles.fieldLabel}>Email</label>
                    <div style={styles.fieldInput}>
                        <InputText
                            value={userEmail}
                            onChange={e => setUserEmail(e.target.value)}
                            placeholder="you@example.com"
                            keyfilter="email"
                            style={styles.input}
                        />
                    </div>
                </div>

                {/* ── Divider: Account ── */}
                <div style={{ margin: "20px 0" }}>
                    <span style={styles.dividerLabel}>Account Details</span>
                </div>

                {/* Username — read only */}
                <div style={styles.fieldRow}>
                    <label style={styles.fieldLabel}>Username</label>
                    <div style={styles.fieldInput}>
                        <InputText
                            value={userName}
                            disabled
                            tooltip="Username cannot be changed"
                            tooltipOptions={{ position: "right" }}
                            style={{ ...styles.input, background: "#F9FAFB", color: "#9CA3AF", cursor: "not-allowed" }}
                        />
                    </div>
                </div>

                <div style={{ borderTop: "1px solid #F3F4F6", margin: "20px 0" }} />

                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                    <Checkbox
                        inputId="chk-pw"
                        checked={isPasswordChange}
                        onChange={e => {
                            setIsPasswordChange(e.checked);
                            if (!e.checked) {
                                setPassword("");
                                setNewPassword("");
                                setConfirmPassword("");
                            }
                        }}
                    />
                    <label
                        htmlFor="chk-pw"
                        style={{ fontFamily: "var(--font-sans)", fontSize: 14, fontWeight: 500, color: "#374151", cursor: "pointer", userSelect: "none" }}
                    >
                        Change Password
                    </label>
                </div>

                {/* Password fields — only when checkbox ticked */}
                {isPasswordChange && (
                    <div style={{ marginTop: 20, paddingTop: 20, borderTop: "1px solid #F3F4F6" }}>

                        {/* Current Password */}
                        <div style={styles.fieldRow}>
                            <label style={styles.fieldLabel}>
                                Current Password <span style={{ color: "#EF4444" }}>*</span>
                            </label>
                            <div style={styles.fieldInput}>
                                <Password
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    placeholder="Enter current password"
                                    feedback={false}
                                    toggleMask
                                    inputStyle={styles.input}
                                    style={{ width: "100%" }}
                                />
                            </div>
                        </div>

                        {/* New Password */}
                        <div style={styles.fieldRow}>
                            <label style={styles.fieldLabel}>
                                New Password <span style={{ color: "#EF4444" }}>*</span>
                            </label>
                            <div style={styles.fieldInput}>
                                <Password
                                    value={newPassword}
                                    onChange={e => setNewPassword(e.target.value)}
                                    placeholder="Enter new password"
                                    feedback={true}
                                    toggleMask
                                    inputStyle={styles.input}
                                    style={{ width: "100%" }}
                                />
                            </div>
                        </div>

                        {/* Confirm Password */}
                        <div style={styles.fieldRow}>
                            <label style={styles.fieldLabel}>
                                Confirm Password <span style={{ color: "#EF4444" }}>*</span>
                            </label>
                            <div style={styles.fieldInput}>
                                <Password
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm new password"
                                    feedback={false}
                                    toggleMask
                                    inputStyle={styles.input}
                                    style={{ width: "100%" }}
                                />
                            </div>
                        </div>

                    </div>
                )}
            </div>

            {/* Action buttons */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 }}>
                <Button
                    label="Cancel"
                    icon="pi pi-times"
                    severity="secondary"
                    outlined
                    onClick={handleCancel}
                    style={styles.btnSecondary}
                />
                <Button
                    label="Save Changes"
                    icon="pi pi-check"
                    onClick={handleSave}
                    style={styles.btnPrimary}
                />
            </div>
        </div>
    );
};

const styles = {
    heading: {
        fontFamily: "var(--font-sans)",
        fontSize: 22,
        fontWeight: 700,
        color: "#111827",
        margin: "0 0 6px 0",
    },

    sub: {
        fontFamily: "var(--font-sans)",
        fontSize: 14,
        color: "#6B7280",
        margin: "0 0 24px 0",
    },

    adminActions: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 16px",
        background: "#FEF3C7",
        border: "1px solid #FDE68A",
        borderRadius: 8,
        marginBottom: 20,
    },

    adminActionsLabel: {
        fontFamily: "var(--font-sans)",
        fontSize: 13,
        fontWeight: 600,
        color: "#92400E",
        display: "flex",
        alignItems: "center",
        gap: 6,
    },

    adminBtn: {
        fontFamily: "var(--font-sans)",
        fontSize: 13,
        height: 34,
        borderRadius: 6,
        borderColor: "#F59E0B",
        color: "#92400E",
    },

    card: {
        background: "#ffffff",
        border: "1px solid #E5E7EB",
        borderRadius: 12,
        padding: "28px 32px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    },

    sectionTitle: {
        fontFamily: "var(--font-sans)",
        fontSize: 11,
        fontWeight: 600,
        color: "#9CA3AF",
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        margin: "0 0 16px 0",
    },

    dividerLabel: {
        display: "inline-block",
        fontFamily: "var(--font-sans)",
        fontSize: 11,
        fontWeight: 600,
        color: "#9CA3AF",
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        background: "#ffffff",
        position: "relative",
    },

    fieldRow: {
        display: "flex",
        alignItems: "center",
        gap: 16,
        marginBottom: 16,
    },

    fieldLabel: {
        fontFamily: "var(--font-sans)",
        width: 150,
        minWidth: 130,
        fontSize: 13,
        fontWeight: 500,
        color: "#4B5563",
        flexShrink: 0,
    },

    fieldInput: {
        flex: 1,
    },

    input: {
        fontFamily: "var(--font-sans)",
        fontSize: 13,
        height: 38,
        borderRadius: 6,
        border: "1px solid #D1D5DB",
        width: "100%",
        color: "#111827",
        padding: "0 12px",
    },

    btnSecondary: {
        fontFamily: "var(--font-sans)",
        fontSize: 13,
        height: 38,
        borderRadius: 6,
        borderColor: "#D1D5DB",
        color: "#374151",
    },

    btnPrimary: {
        fontFamily: "var(--font-sans)",
        fontSize: 13,
        height: 38,
        borderRadius: 6,
        background: "#4CAF50",
        borderColor: "#4CAF50",
        color: "#ffffff",
    },
};

export default Profile;
